import React, { useContext, useEffect, useState } from "react";
import { withPrismicUnpublishedPreview } from "gatsby-plugin-prismic-previews";
import styled from "styled-components";
import { Link, graphql } from "gatsby";
import { use100vh } from "react-div-100vh";
import { useAsyncFn } from "react-use";

// Context
import { PageSetup } from "../components/context/page-context";

// SEO
import { PageSeo } from "../components/seo/page-seo";

const Page = styled.div`
  padding: 51px 0 0 0;
  min-height: calc(${props => props.height}px - 50px - 50px);

  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  color: #0b646f;

  & .page-container {
    max-width: 600px;
    margin: 0 auto;
    padding: 0 50px;

    @media (max-width: 768px) {
      padding: 0 20px;
    }
  }

  & h1,
  & h2,
  & p {
    font-size: 22px;
    line-height: 32px;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 16px;
      line-height: 24px;
    }
  }

  & h1 {
    font-size: 32px;
    margin-bottom: 30px;

    @media (max-width: 768px) {
      font-size: 24px;
    }
  }

  & h2 {
    font-size: 24px;
    margin: 30px 0 20px 0;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  }

  & .gift-card-info {
    background: #f5eadf;
    padding: 30px;
    margin: 30px 0;
    border-radius: 8px;

    & .gift-card-code {
      font-family: monospace;
      font-size: 24px;
      font-weight: bold;
      letter-spacing: 2px;
      background: #fff;
      padding: 15px;
      border-radius: 4px;
      text-align: center;
      margin: 20px 0;

      @media (max-width: 768px) {
        font-size: 18px;
        padding: 10px;
      }
    }

    & .gift-card-balance {
      font-size: 28px;
      font-weight: bold;
      color: #0b646f;
      text-align: center;
      margin: 20px 0;

      @media (max-width: 768px) {
        font-size: 24px;
      }
    }
  }

  & .instructions {
    text-align: left;
    margin: 30px 0;

    & ul {
      padding-left: 20px;

      & li {
        margin: 10px 0;
        font-size: 16px;
        line-height: 24px;
      }
    }
  }

  & .back-link {
    text-align: center;
    margin-top: 40px;

    & a {
      color: #debb9b;
      text-decoration: none;
      font-weight: bold;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const GiftCard = ({ location, data }) => {
  const [pageSetup, setPageSetup] = useContext(PageSetup);
  const height = use100vh();
  const [giftCardData, setGiftCardData] = useState(null);

  const [fetchState, fetchGiftCard] = useAsyncFn(async giftCardId => {
    const response = await fetch(`/.netlify/functions/get-gift-card`, {
      method: "POST",
      body: JSON.stringify({
        giftCardId: giftCardId,
      }),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch gift card");
    }

    const result = await response.json();
    setGiftCardData(result);
    return result;
  }, []);

  useEffect(() => {
    setPageSetup({
      pageTitle: `Gift Card`,
      pageColor: `#DDE1D4`,
      pageType: `not-found`,
      filterArtists: [],
      selectedArtists: [],
      sortOption: null,
      priceOption: null,
      artistOption: [],
      applyFilter: false,
    });
  }, []);

  useEffect(() => {
    // Extract gift card ID from URL
    const pathParts = location.pathname.split("/");
    const giftCardId = pathParts[pathParts.length - 1];

    if (giftCardId) {
      fetchGiftCard(giftCardId);
    }
  }, [location.pathname, fetchGiftCard]);

  if (fetchState.loading) {
    return (
      <Page height={height}>
        <div className="page-container">
          <h1>Loading Gift Card...</h1>
        </div>
      </Page>
    );
  }

  if (fetchState.error || !giftCardData) {
    return (
      <>
        <PageSeo
          seoTitle={`Gift Card Not Found`}
          seoText={null}
          seoImage={null}
        />
        <Page height={height}>
          <div className="page-container">
            <h1>Gift Card Not Found</h1>
            <p>The gift card you're looking for could not be found.</p>
            <div className="back-link">
              <Link to={`/shop/`}>Continue Shopping</Link>
            </div>
          </div>
        </Page>
      </>
    );
  }

  return (
    <>
      <PageSeo
        seoTitle={`Gift Card - ${giftCardData.code}`}
        seoText={`View your House of Voltaire gift card details`}
        seoImage={null}
      />
      <Page height={height}>
        <div className="page-container">
          <h1>Your Gift Card</h1>

          <div className="gift-card-info">
            <h2>Gift Card Details</h2>
            <div className="gift-card-code">{giftCardData.code}</div>
            <div className="gift-card-balance">
              {giftCardData.currency} {giftCardData.balance}
            </div>
            <p>
              <strong>Status:</strong>{" "}
              {giftCardData.disabled_at ? "Disabled" : "Active"}
            </p>
            {giftCardData.expires_on && (
              <p>
                <strong>Expires:</strong>{" "}
                {new Date(giftCardData.expires_on).toLocaleDateString()}
              </p>
            )}
          </div>

          <div className="instructions">
            <h2>How to Use Your Gift Card</h2>
            <ul>
              <li>Add items to your cart on our website</li>
              <li>
                At checkout, enter your gift card code in the "Gift card or
                discount code" field
              </li>
              <li>Click "Apply" to use your gift card balance</li>
              <li>Complete your purchase with any remaining balance</li>
            </ul>
          </div>

          <div className="instructions">
            <h2>Important Information</h2>
            <ul>
              <li>Gift cards do not expire</li>
              <li>Gift cards cannot be exchanged for cash</li>
              <li>
                If your order total is less than your gift card balance, the
                remaining amount will stay on your gift card
              </li>
              <li>
                If your order total is more than your gift card balance, you can
                pay the difference with another payment method
              </li>
            </ul>
          </div>

          <div className="back-link">
            <Link to={`/shop/`}>Start Shopping</Link>
          </div>
        </div>
      </Page>
    </>
  );
};

export default withPrismicUnpublishedPreview(GiftCard);
